/**
 * A department's score (CFO plan §5, Lj: "each department has its own scoring system based on its 3 most important
 * metrics"): three metrics picked from DEPT_METRICS (department doc `scoreMetrics`, else the department's defaults),
 * each for a day against your own rolling 30-day baseline. Revenue-first; never flow / plan-order metrics (they
 * measure the list, not the work — scores memory 2026-09-24). Pure — no I/O.
 *
 * Time comes from task time entries (by entry date), each under its task's department (workdept.js, D5). A task
 * is "overhead" when it's a quick task / setup / cleanup (travel, a material run): the rest is the job itself.
 */
import { workDepartments } from './workdept.js';
import { addDaysStr } from './phonestats.js';
import { canonTag } from './tags.js';
import { earnedByJobType } from './jobpay.js';

const H = 3600000;
/**
 * Overhead on site — the time that isn't the job itself (Field plan, Lj to confirm: setup, cleanup, material / store
 * runs AND travel all count): a quick task, a setup / cleanup checklist, or a task tagged #materials or #travel.
 */
export const OVERHEAD_TAGS = Object.freeze(['materials', 'travel']);
export const isOverheadTask = (t) => !!(t?.quickTaskId || t?.bucket || (Array.isArray(t?.tags) && t.tags.some((x) => OVERHEAD_TAGS.includes(canonTag(String(x))))));
const isOverhead = isOverheadTask;

/** The menu. value(day) → number | null (null = nothing to measure); higher is better for every one. */
export const DEPT_METRICS = Object.freeze({
  onEstimate: { label: 'Efficiency', unit: '%', hint: 'estimated ÷ actual hours on tasks finished' },
  productiveShare: { label: 'Productive share', unit: '%', hint: 'job-task hours ÷ all hours (setup, cleanup, travel, runs are the rest)' },
  earnedPerHour: { label: 'Earned per hour', unit: '$/h', hint: 'fixed / product jobs: the labour in the price (bid labour, else contract × labour %) × share of the estimate finished; T&M jobs: hours × the bill rate — ÷ hours' },
  tasksDone: { label: 'Tasks done', unit: '', hint: 'tasks finished (not checklists)' },
  hoursWorked: { label: 'Hours worked', unit: 'h', hint: 'hours on tasks' },
});

export const DEFAULT_SCORE_METRICS = Object.freeze({
  field: ['onEstimate', 'productiveShare', 'earnedPerHour'],
  _: ['hoursWorked', 'tasksDone', 'onEstimate'],
});

/** The department's three: its own pick (unknown ones dropped), else its defaults. */
export function scoreMetricsFor(dept) {
  const own = Array.isArray(dept?.scoreMetrics) ? dept.scoreMetrics.filter((k) => DEPT_METRICS[k]).slice(0, 3) : [];
  if (own.length) return own;
  const isField = dept && (dept.id === 'field' || String(dept.name || '').trim().toLowerCase() === 'field');
  return [...(isField ? DEFAULT_SCORE_METRICS.field : DEFAULT_SCORE_METRICS._)];
}

/**
 * Totals for one department over [from, to] (inclusive dates): hours, overhead hours, estimates, earned, done.
 * `earned` / `valued` are Earned per hour's count by job type — jobpay.js earnedByJobType, the same one the Field goal,
 * the dashboard and the Daily Log read (v0.1.261; it was contract value × share, the whole price, before). `jobs` =
 * the books' jobs (bid labour, contract), `policy` = the overhead policy (labour %).
 */
export function deptTotals({ tasks = [], projects = [], departments = [], departmentId, from, to, departmentMode = true, jobs = [], policy = null }) {
  const w = workDepartments({ tasks, projects, departments, departmentMode });
  const inRange = (d) => d && d >= from && d <= to;
  const parents = new Set(tasks.filter((t) => t.parentId && t.estimatedMinutes > 0).map((t) => t.parentId));
  const E = earnedByJobType({ tasks, projects, departments, jobs, policy, from, to, entryIn: (e, t) => w.forEntry(e, t)?.id === departmentId, taskIn: (t) => w.forTask(t)?.id === departmentId });
  const out = { ms: 0, overheadMs: 0, estMin: 0, actualMs: 0, earned: E.earned, valued: E.valued, done: 0 };
  for (const t of tasks) {
    for (const e of t.timeEntries || []) {
      if (!inRange(e.date) || !(e.duration > 0)) continue;
      if (w.forEntry(e, t)?.id !== departmentId) continue;
      out.ms += e.duration;
      if (isOverhead(t)) out.overheadMs += e.duration;
    }
    const doneDay = t.status === 'done' && t.completedAt ? new Date(t.completedAt) : null;
    const doneDate = doneDay ? `${doneDay.getFullYear()}-${String(doneDay.getMonth() + 1).padStart(2, '0')}-${String(doneDay.getDate()).padStart(2, '0')}` : null;
    if (!inRange(doneDate) || isOverhead(t) || w.forTask(t)?.id !== departmentId) continue;
    out.done += 1;
    // estimate vs actual: real stretches only (a routine step or a card ticked in seconds would swamp it)
    // a parent whose subtasks carry the estimate is counted through them, not on top
    if (t.estimatedMinutes > 0 && t.timeSpent >= 60000 && !t.runId && !parents.has(t.id)) { out.estMin += t.estimatedMinutes; out.actualMs += t.timeSpent; }
  }
  return out;
}

/** One metric's value from totals (null = nothing to measure, or nothing priced yet for earnedPerHour). */
export function metricValue(key, tot) {
  const round1 = (v) => Math.round(v * 10) / 10;
  switch (key) {
    case 'onEstimate': return tot.actualMs > 0 ? Math.round(((tot.estMin * 60000) / tot.actualMs) * 100) : null;
    case 'productiveShare': return tot.ms > 0 ? Math.round(((tot.ms - tot.overheadMs) / tot.ms) * 100) : null;
    case 'earnedPerHour': return tot.valued && tot.ms > 0 ? Math.round(tot.earned / (tot.ms / H)) : null;
    case 'tasksDone': return tot.done;
    case 'hoursWorked': return tot.ms > 0 ? round1(tot.ms / H) : 0;
    default: return null;
  }
}

/**
 * The department's score for a day: its three metrics, each with today's value, the 30-day baseline (the 30 days
 * before, as one period averaged per worked day for counts/hours), and a 0–100 mark (baseline = 70, double it ≈ 100).
 * score = the mean of the marks that have a value. → { departmentId, date, metrics: [...], score }
 */
export function deptScore({ tasks = [], projects = [], departments = [], dept, date, departmentMode = true, jobs = [], policy = null }) {
  const id = dept?.id;
  const keys = scoreMetricsFor(dept);
  const day = deptTotals({ tasks, projects, departments, departmentId: id, from: date, to: date, departmentMode, jobs, policy });
  const base = deptTotals({ tasks, projects, departments, departmentId: id, from: addDaysStr(date, -30), to: addDaysStr(date, -1), departmentMode, jobs, policy });
  // counts and hours: per day worked in the baseline, so a day compares with a day
  const workedDays = new Set();
  for (const t of tasks) for (const e of t.timeEntries || []) if (e.date >= addDaysStr(date, -30) && e.date <= addDaysStr(date, -1) && e.duration > 0) workedDays.add(e.date);
  const perDay = Math.max(1, workedDays.size);
  const metrics = keys.map((key) => {
    const value = metricValue(key, day);
    let baseline = metricValue(key, base);
    if (baseline != null && (key === 'tasksDone' || key === 'hoursWorked')) baseline = Math.round((baseline / perDay) * 10) / 10;
    const mark = value == null ? null : baseline ? Math.max(0, Math.min(100, Math.round(70 * (value / baseline) ** 0.5))) : 70;
    return { key, ...DEPT_METRICS[key], value, baseline, mark, needs: key === 'earnedPerHour' && value == null && day.ms > 0 ? 'a contract value, bid or bill rate on the job' : null };
  });
  const marked = metrics.filter((m) => m.mark != null);
  return { departmentId: id, date, metrics, score: marked.length ? Math.round(marked.reduce((a, m) => a + m.mark, 0) / marked.length) : null };
}
